import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Heart,
  Bookmark,
  Flag,
  Copy,
  Wand2,
  Loader2,
  X,
  ImageIcon,
  Eye,
  ThumbsUp,
  Clock,
  Grid3X3,
  LayoutList,
  Search,
  AlertTriangle,
  ArrowRight,
  ImagePlus,
  Paintbrush,
} from 'lucide-react';
import { galleryAPI, type GalleryWork, api } from '../services/api';
import toast from '../components/Toast';

/** modelId → 模型展示名映射；列表加载时一次性构建，卡片和详情复用 */
const modelNameMap = new Map<string, string>();
import { getActiveToken } from '../utils/authSession';
import { LazyImage } from '../components/LazyImage';
import { formatBeijingDate } from '../utils/time';
import { useSiteName } from '../platform/useSiteName';
import { resolveBrowserMediaUrl } from '../utils/storageUrl';

const SORT_OPTIONS = [
  { key: 'latest', label: '最新作品', icon: Clock },
  { key: 'hot', label: '热门作品', icon: ThumbsUp },
];

const FILTER_OPTIONS = [
  { key: 'all', label: '全部作品', icon: Grid3X3 },
  { key: 'likes', label: '我的点赞', icon: Heart },
  { key: 'favorites', label: '我的收藏', icon: Bookmark },
];

// 复用模板广场的分类体系
const CATEGORIES = [
  { value: 'all', label: '全部' },
  { value: 'general', label: '综合' },
  { value: 'ecommerce', label: '电商与产品' },
  { value: 'poster', label: '品牌海报' },
  { value: 'typography', label: '创意字体' },
  { value: 'infographic', label: '知识科普' },
  { value: 'ui', label: 'UI界面' },
  { value: 'photography', label: '真实摄影' },
  { value: 'character', label: '角色一致性' },
  { value: 'edit', label: '图片编辑' },
  { value: 'art', label: '艺术创作' },
  { value: 'portrait', label: '人像' },
  { value: 'landscape', label: '风景' },
  { value: 'anime', label: '动漫' },
  { value: 'architecture', label: '建筑' },
  { value: 'food', label: '美食' },
  { value: 'scifi', label: '科幻' },
  { value: 'nature', label: '自然' },
  { value: 'other', label: '其他' },
];

const MODES: Record<string, string> = {
  text2img: '文生图',
  img2img: '参考图',
  inpaint: '局部重绘',
};

export default function GalleryPage() {
  const navigate = useNavigate();
  const siteName = useSiteName();
  const [works, setWorks] = useState<GalleryWork[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSort, setActiveSort] = useState('latest');
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedWork, setSelectedWork] = useState<GalleryWork | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [interactions, setInteractions] = useState<Record<string, { isLiked: boolean; isFavorited: boolean }>>({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  // AI融合相关状态
  const [showRemixModal, setShowRemixModal] = useState(false);
  const [remixRequirement, setRemixRequirement] = useState('');
  const [remixLoading, setRemixLoading] = useState(false);
  const [remixPrompt, setRemixPrompt] = useState('');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  // 加载公开模型列表构建 modelId→名称 映射，供卡片和详情展示模型名
  useEffect(() => {
    api.getModels().then((res) => {
      for (const m of res.data) modelNameMap.set(m.id, m.name);
    }).catch(() => {});
  }, []);

  const loadWorks = useCallback(async () => {
    const isLoggedIn = !!getActiveToken();
    
    // 如果未登录但选择了需要登录的筛选条件，显示空列表
    if ((activeFilter === 'likes' || activeFilter === 'favorites') && !isLoggedIn) {
      setWorks([]);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      let res;
      if (activeFilter === 'likes') {
        res = await galleryAPI.getMyLikes({ page, limit: 24 });
      } else if (activeFilter === 'favorites') {
        res = await galleryAPI.getMyFavorites({ page, limit: 24 });
      } else {
        res = await galleryAPI.getPublicWorks({
          sort: activeSort as 'latest' | 'hot',
          category: activeCategory,
          page,
          limit: 24,
        });
      }
      setWorks(res.data);
      setTotalPages(res.pagination.totalPages);
    } catch (e) {
      toast.error('加载作品失败');
    } finally {
      setLoading(false);
    }
  }, [activeSort, activeCategory, activeFilter, page]);

  useEffect(() => {
    loadWorks();
  }, [loadWorks]);

  // 加载互动状态
  useEffect(() => {
    const loadInteractions = async () => {
      const token = getActiveToken();
      if (!token || works.length === 0) return;

      const newInteractions: Record<string, { isLiked: boolean; isFavorited: boolean }> = {};
      await Promise.all(
        works.map(async (work) => {
          try {
            const res = await galleryAPI.getInteractions(work.id);
            newInteractions[work.id] = res;
          } catch {
            // ignore
          }
        })
      );
      setInteractions((prev) => ({ ...prev, ...newInteractions }));
    };

    loadInteractions();
  }, [works]);

  const handleOpenWork = async (work: GalleryWork) => {
    setDetailLoading(true);
    try {
      const detail = await galleryAPI.getPublicWork(work.id);
      setSelectedWork(detail);
      // 加载互动状态
      const token = getActiveToken();
      if (token) {
        const res = await galleryAPI.getInteractions(work.id);
        setInteractions((prev) => ({ ...prev, [work.id]: res }));
      }
    } catch {
      toast.error('加载作品详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleLike = async (workId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const token = getActiveToken();
    if (!token) {
      toast.error('请先登录');
      return;
    }
    try {
      const current = interactions[workId]?.isLiked;
      if (current) {
        await galleryAPI.unlikeWork(workId);
        setInteractions((prev) => ({
          ...prev,
          [workId]: { ...prev[workId], isLiked: false },
        }));
        setWorks((prev) =>
          prev.map((w) => (w.id === workId ? { ...w, likeCount: w.likeCount - 1 } : w))
        );
        if (selectedWork?.id === workId) {
          setSelectedWork((prev) => (prev ? { ...prev, likeCount: prev.likeCount - 1 } : prev));
        }
      } else {
        await galleryAPI.likeWork(workId);
        setInteractions((prev) => ({
          ...prev,
          [workId]: { ...prev[workId], isLiked: true },
        }));
        setWorks((prev) =>
          prev.map((w) => (w.id === workId ? { ...w, likeCount: w.likeCount + 1 } : w))
        );
        if (selectedWork?.id === workId) {
          setSelectedWork((prev) => (prev ? { ...prev, likeCount: prev.likeCount + 1 } : prev));
        }
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '操作失败';
      toast.error(msg);
    }
  };

  const handleFavorite = async (workId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const token = getActiveToken();
    if (!token) {
      toast.error('请先登录');
      return;
    }
    try {
      const current = interactions[workId]?.isFavorited;
      if (current) {
        await galleryAPI.unfavoriteWork(workId);
        setInteractions((prev) => ({
          ...prev,
          [workId]: { ...prev[workId], isFavorited: false },
        }));
      } else {
        await galleryAPI.favoriteWork(workId);
        setInteractions((prev) => ({
          ...prev,
          [workId]: { ...prev[workId], isFavorited: true },
        }));
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '操作失败';
      toast.error(msg);
    }
  };

  const handleReport = async () => {
    if (!selectedWork) return;
    try {
      await galleryAPI.reportWork(selectedWork.id, reportReason);
      setShowReportModal(false);
      setReportReason('');
      setSelectedWork(null);
      toast.success('举报成功，我们会尽快处理');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '举报失败';
      toast.error(msg);
    }
  };

  const handleCopyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
    setCopiedPrompt(true);
    setTimeout(() => setCopiedPrompt(false), 2000);
  };

  // AI融合同款生成 - 流式输出
  const handleRemix = async () => {
    if (!selectedWork || !remixRequirement.trim()) return;
    const token = getActiveToken();
    if (!token) {
      toast.error('请先登录');
      return;
    }
    setRemixLoading(true);
    setRemixPrompt(''); // 清空之前的提示词

    try {
      await galleryAPI.remixWorkStream(
        selectedWork.id,
        remixRequirement,
        (chunk) => {
          // 流式接收内容
          setRemixPrompt((prev) => prev + chunk);
        },
        (error) => {
          // 错误处理
          toast.error(error);
        },
        () => {
          // 完成回调
          setRemixLoading(false);
        }
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '融合生成失败';
      toast.error(msg);
      setRemixLoading(false);
    }
  };

  // 使用融合后的提示词生成图片
  const handleGenerateRemix = async () => {
    if (!selectedWork || !remixPrompt.trim()) return;

    // 文生图：直接调用 API 生成
    if (selectedWork.mode === 'text2img') {
      setIsGeneratingImage(true);
      try {
        await api.generateImage({
          model: selectedWork.model,
          prompt: remixPrompt,
          size: selectedWork.size,
          quality: selectedWork.quality,
          mode: selectedWork.mode,
          moderation: 'low',
        });
        setShowRemixModal(false);
        setRemixRequirement('');
        setRemixPrompt('');
        navigate('/');
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : '生成失败';
        toast.error(msg);
        setIsGeneratingImage(false);
      }
    }
    // 图生图：跳转到首页，填充提示词和模式，提示用户上传参考图
    else if (selectedWork.mode === 'img2img') {
      setShowRemixModal(false);
      setRemixRequirement('');
      setRemixPrompt('');
      navigate('/', {
        state: {
          mode: 'img2img',
          prompt: remixPrompt,
          model: selectedWork.model,
          size: selectedWork.size,
          quality: selectedWork.quality,
        },
      });
      toast.success('已切换到参考图生图模式，请上传参考图');
    }
  };

  // 打开AI融合弹窗
  const openRemixModal = () => {
    const token = getActiveToken();
    if (!token) {
      toast.error('请先登录');
      return;
    }
    setRemixRequirement('');
    setRemixPrompt('');
    setShowRemixModal(true);
  };

  // 作为参考图进行迭代
  const handleUseAsReference = (work: GalleryWork) => {
    if (!work.resultUrl) {
      toast.error('图片尚未生成完成');
      return;
    }

    navigate('/', {
      state: {
        continueIteration: true,
        jobId: work.id,
        imageUrl: work.resultUrl,
        prompt: work.prompt,
        mode: 'img2img',
      },
    });
  };

  // 局部重绘
  const handleInpaint = (work: GalleryWork) => {
    if (!work.resultUrl) {
      toast.error('图片尚未生成完成');
      return;
    }

    navigate('/', {
      state: {
        continueIteration: true,
        jobId: work.id,
        imageUrl: work.resultUrl,
        prompt: work.prompt,
        mode: 'inpaint',
      },
    });
  };

  const handleGenerateSame = async (work: GalleryWork) => {
    // 文生图：直接调用 API 生成
    if (work.mode === 'text2img') {
      try {
        await api.generateImage({
          model: work.model,
          prompt: work.prompt || '',
          size: work.size,
          quality: work.quality,
          mode: work.mode as 'text2img' | 'img2img' | 'inpaint',
          moderation: 'low',
        });
        navigate('/');
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : '同款生成失败';
        toast.error(msg);
      }
    }
    // 图生图：跳转到首页，填充提示词和模式，提示用户上传参考图
    else if (work.mode === 'img2img') {
      navigate('/', {
        state: {
          mode: 'img2img',
          prompt: work.prompt || '',
          model: work.model,
          size: work.size,
          quality: work.quality,
        },
      });
      toast.success('已切换到参考图生图模式，请上传参考图');
    }
  };

  const filteredWorks = searchQuery
    ? works.filter(
        (w) =>
          w.publicTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (w.prompt && w.prompt.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : works;

  return (
    <div className="min-h-screen bg-light-bg">
      {/* Header */}
      <div className="bg-white border-b border-light-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-light-text hover:text-accent-blue transition-colors"
              >
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold">{siteName}</span>
              </button>
              <span className="text-light-muted">/</span>
              <span className="font-medium text-light-text">公共作品广场</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="p-2 rounded-xl border border-light-border hover:bg-light-hover transition-colors"
              >
                {viewMode === 'grid' ? <LayoutList className="w-4 h-4" /> : <Grid3X3 className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Sort */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <div className="relative flex-1 max-w-full sm:max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索作品..."
                className="w-full pl-12 pr-4 py-3 bg-white border border-light-border rounded-xl text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-light-muted hover:text-light-text"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 scrollbar-thin">
              {SORT_OPTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.key}
                    onClick={() => {
                      setActiveSort(s.key);
                      setPage(1);
                    }}
                    className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-sm font-medium flex items-center gap-1.5 sm:gap-2 transition-all whitespace-nowrap shrink-0 ${
                      activeSort === s.key
                        ? 'bg-accent-blue text-white shadow-md'
                        : 'bg-white text-light-secondary border border-light-border hover:border-accent-blue hover:text-accent-blue'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{s.label}</span>
                    <span className="sm:hidden">{s.label.slice(0, 2)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filter Tabs - 筛选我的点赞/收藏 */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin -mx-4 px-4 sm:mx-0 sm:px-0">
            {FILTER_OPTIONS.map((f) => {
              const Icon = f.icon;
              const isLoggedIn = !!getActiveToken();
              const requiresAuth = f.key === 'likes' || f.key === 'favorites';
              return (
                <button
                  key={f.key}
                  onClick={() => {
                    if (requiresAuth && !isLoggedIn) {
                      toast.error('请先登录');
                      return;
                    }
                    setActiveFilter(f.key);
                    setPage(1);
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all whitespace-nowrap shrink-0 ${
                    activeFilter === f.key
                      ? 'bg-accent-blue text-white shadow-md'
                      : 'bg-white text-light-text border border-light-border hover:bg-light-hover'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${requiresAuth && !isLoggedIn ? 'opacity-50' : ''}`} />
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* Category Filter - 复用模板广场的分类瀑布流样式 */}
          {activeFilter === 'all' && (
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => {
                    setActiveCategory(cat.value);
                    setPage(1);
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap shrink-0 ${
                    activeCategory === cat.value
                      ? 'bg-accent-blue text-white shadow-md'
                      : 'bg-white text-light-text border border-light-border hover:bg-light-hover'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Works */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
          </div>
        ) : filteredWorks.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon className="w-12 h-12 text-light-muted mx-auto mb-4" />
            <p className="text-light-muted mb-4">
              {activeFilter === 'likes'
                ? '暂无点赞的作品'
                : activeFilter === 'favorites'
                ? '暂无收藏的作品'
                : '暂无作品'}
            </p>
            {(activeFilter === 'likes' || activeFilter === 'favorites') && !getActiveToken() ? (
              <button onClick={() => navigate('/')} className="btn-primary">
                去登录
              </button>
            ) : (
              <button onClick={() => navigate('/')} className="btn-primary">
                去生成
              </button>
            )}
          </div>
        ) : (
          <>
            <div
              className={
                viewMode === 'grid'
                  ? 'columns-2 sm:columns-2 lg:columns-3 xl:columns-4 gap-3 sm:gap-4 space-y-3 sm:space-y-4'
                  : 'flex flex-col gap-3'
              }
            >
              {filteredWorks.map((work) => (
                <div
                  key={work.id}
                  onClick={() => handleOpenWork(work)}
                  className={`group bg-white border border-light-border rounded-2xl overflow-hidden hover:shadow-lg hover:border-accent-blue/30 transition-all cursor-pointer break-inside-avoid mb-4 ${
                    viewMode === 'list' ? 'flex items-center gap-4 p-4' : ''
                  }`}
                >
                  {viewMode === 'grid' && (
                    <div className="bg-light-hover relative overflow-hidden">
                      {work.resultUrl ? (
                        <LazyImage
                          src={resolveBrowserMediaUrl(work.publicImageUrl || work.resultUrl)}
                          alt={work.publicTitle}
                          className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="aspect-square w-full flex items-center justify-center">
                          <ImageIcon className="w-10 h-10 text-light-muted" />
                        </div>
                      )}
                      {/* Overlay actions */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => handleLike(work.id, e)}
                            className="p-2 rounded-xl bg-white/90 backdrop-blur-sm hover:bg-white transition-colors"
                          >
                            <Heart
                              className={`w-4 h-4 ${
                                interactions[work.id]?.isLiked
                                  ? 'fill-red-500 text-red-500'
                                  : 'text-light-muted'
                              }`}
                            />
                          </button>
                          <button
                            onClick={(e) => handleFavorite(work.id, e)}
                            className="p-2 rounded-xl bg-white/90 backdrop-blur-sm hover:bg-white transition-colors"
                          >
                            <Bookmark
                              className={`w-4 h-4 ${
                                interactions[work.id]?.isFavorited
                                  ? 'fill-accent-blue text-accent-blue'
                                  : 'text-light-muted'
                              }`}
                            />
                          </button>
                        </div>
                        <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-lg flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {work.viewCount}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className={viewMode === 'grid' ? 'p-4' : 'flex-1'}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-light-text text-sm line-clamp-1">{work.publicTitle}</h3>
                        <p className="text-xs text-light-muted mt-0.5 line-clamp-1">{work.publicDescription || work.prompt || ''}</p>
                      </div>
                      {viewMode === 'list' && (
                        <div className="flex items-center gap-2">
                          <button onClick={(e) => handleLike(work.id, e)}>
                            <Heart
                              className={`w-4 h-4 ${
                                interactions[work.id]?.isLiked
                                  ? 'fill-red-500 text-red-500'
                                  : 'text-light-muted'
                              }`}
                            />
                          </button>
                          <button onClick={(e) => handleFavorite(work.id, e)}>
                            <Bookmark
                              className={`w-4 h-4 ${
                                interactions[work.id]?.isFavorited
                                  ? 'fill-accent-blue text-accent-blue'
                                  : 'text-light-muted'
                              }`}
                            />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-2 text-xs text-light-muted">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <span className="px-1.5 py-0.5 bg-light-hover rounded shrink-0">{MODES[work.mode] || work.mode}</span>
                        <span className="truncate">{work.size}</span>
                        {modelNameMap.get(work.model) && (
                          <span className="truncate text-slate-400">{modelNameMap.get(work.model)}</span>
                        )}
                        <span className="flex items-center gap-1 shrink-0">
                          <Heart className="w-3 h-3" />
                          {work.likeCount}
                        </span>
                      </div>
                      <span className="truncate shrink-0 max-w-[80px]">{work.user.name}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-4 py-2 bg-white border border-light-border rounded-xl text-sm disabled:opacity-40 hover:border-accent-blue transition-colors"
                >
                  上一页
                </button>
                <span className="text-sm text-light-secondary px-3">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-4 py-2 bg-white border border-light-border rounded-xl text-sm disabled:opacity-40 hover:border-accent-blue transition-colors"
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Work Detail Modal */}
      {selectedWork && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedWork(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {detailLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
              </div>
            ) : (
              <>
                <div className="sticky top-0 bg-white border-b border-light-border px-6 py-4 flex items-center justify-between z-10 shrink-0">
                  <div>
                    <h2 className="text-lg font-semibold text-light-text">{selectedWork.publicTitle}</h2>
                    <p className="text-xs text-light-muted">by {selectedWork.user.name}</p>
                  </div>
                  <button
                    onClick={() => setSelectedWork(null)}
                    className="p-2 hover:bg-light-hover rounded-xl transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 左侧：图片 */}
                    <div>
                      <div className="bg-light-hover rounded-2xl overflow-hidden relative group/modal-image">
                        {selectedWork.resultUrl ? (
                          <>
                            <img
                              src={resolveBrowserMediaUrl(selectedWork.publicImageUrl || selectedWork.resultUrl)}
                              alt={selectedWork.publicTitle}
                              className="w-full h-auto object-contain"
                            />
                            {/* 遮罩层 */}
                            <div className="absolute inset-0 bg-black/0 group-hover/modal-image:bg-black/40 transition-colors" />
                            {/* 桌面端：悬停显示按钮 */}
                            <div className="absolute inset-0 hidden md:flex items-center justify-center gap-3 opacity-0 group-hover/modal-image:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleUseAsReference(selectedWork)}
                                className="px-4 py-2 bg-white/90 hover:bg-white rounded-lg text-sm font-medium text-accent-blue transition-colors flex items-center gap-1.5"
                              >
                                <ImagePlus className="w-4 h-4" />作为参考图
                              </button>
                              <button
                                onClick={() => handleInpaint(selectedWork)}
                                className="px-4 py-2 bg-white/90 hover:bg-white rounded-lg text-sm font-medium text-purple-600 transition-colors flex items-center gap-1.5"
                              >
                                <Paintbrush className="w-4 h-4" />局部重绘
                              </button>
                            </div>
                            {/* 移动端：始终显示小按钮 */}
                            <div className="absolute bottom-3 right-3 flex md:hidden gap-2">
                              <button
                                onClick={() => handleUseAsReference(selectedWork)}
                                className="p-2 bg-black/50 rounded-lg text-white"
                              >
                                <ImagePlus className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleInpaint(selectedWork)}
                                className="p-2 bg-black/50 rounded-lg text-white"
                              >
                                <Paintbrush className="w-5 h-5" />
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="w-full aspect-square flex items-center justify-center">
                            <ImageIcon className="w-12 h-12 text-light-muted" />
                          </div>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="flex items-center justify-center gap-6 text-sm text-light-secondary mt-4">
                        <span className="flex items-center gap-1.5">
                          <Eye className="w-4 h-4" />
                          {selectedWork.viewCount} 浏览
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Heart className="w-4 h-4" />
                          {selectedWork.likeCount} 点赞
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" />
                          {selectedWork.publishedAt
                            ? formatBeijingDate(selectedWork.publishedAt)
                            : ''}
                        </span>
                      </div>
                    </div>

                    {/* 右侧：信息和操作 */}
                    <div className="space-y-4">
                      {/* Info */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="p-3 bg-light-hover rounded-xl">
                          <div className="text-light-muted text-xs mb-1">模式</div>
                          <div className="font-medium">{MODES[selectedWork.mode] || selectedWork.mode}</div>
                        </div>
                        <div className="p-3 bg-light-hover rounded-xl">
                          <div className="text-light-muted text-xs mb-1">模型</div>
                          <div className="font-medium">{modelNameMap.get(selectedWork.model) || selectedWork.model}</div>
                        </div>
                        <div className="p-3 bg-light-hover rounded-xl">
                          <div className="text-light-muted text-xs mb-1">尺寸</div>
                          <div className="font-medium">{selectedWork.size}</div>
                        </div>
                        <div className="p-3 bg-light-hover rounded-xl">
                          <div className="text-light-muted text-xs mb-1">质量</div>
                          <div className="font-medium">{selectedWork.quality}</div>
                        </div>
                      </div>

                      {/* Prompt */}
                      {selectedWork.isPromptPublic && selectedWork.prompt && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium text-light-text">提示词</h3>
                            <button
                              onClick={() => handleCopyPrompt(selectedWork.prompt!)}
                              className="text-xs text-accent-blue hover:underline flex items-center gap-1"
                            >
                              <Copy className="w-3 h-3" />
                              {copiedPrompt ? '已复制' : '复制'}
                            </button>
                          </div>
                          <div className="p-4 bg-light-hover rounded-xl text-sm text-light-text whitespace-pre-wrap max-h-40 overflow-y-auto">
                            {selectedWork.prompt}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-2 pt-2">
                        {/* 同款生成按钮：仅文生图和图生图支持，局部重绘不支持 */}
                        {selectedWork.mode !== 'inpaint' && (
                          <button
                            onClick={() => handleGenerateSame(selectedWork)}
                            className="btn-primary flex items-center gap-2 text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-2.5"
                          >
                            <Wand2 className="w-4 h-4" />
                            <span className="hidden sm:inline">同款生成</span>
                            <span className="sm:hidden">同款</span>
                          </button>
                        )}
                        <button
                          onClick={openRemixModal}
                          className="px-3 sm:px-4 py-2 sm:py-2.5 bg-accent-blue/10 text-accent-blue border border-accent-blue/30 hover:bg-accent-blue/20 font-medium rounded-xl flex items-center gap-2 transition-colors text-sm sm:text-base"
                        >
                          <Sparkles className="w-4 h-4" />
                          <span className="hidden sm:inline">AI融合同款</span>
                          <span className="sm:hidden">融合</span>
                        </button>
                        {selectedWork.isPromptPublic && selectedWork.prompt && (
                          <button
                            onClick={() => handleCopyPrompt(selectedWork.prompt!)}
                            className="px-3 sm:px-4 py-2 sm:py-2.5 border border-light-border rounded-xl text-sm font-medium hover:bg-light-hover transition-colors flex items-center gap-2"
                          >
                            <Copy className="w-4 h-4" />
                            <span className="hidden sm:inline">{copiedPrompt ? '已复制' : '复制提示词'}</span>
                            <span className="sm:hidden">{copiedPrompt ? '已复制' : '复制'}</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleLike(selectedWork.id)}
                          className={`px-3 sm:px-4 py-2 sm:py-2.5 border rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                            interactions[selectedWork.id]?.isLiked
                              ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                              : 'border-light-border hover:bg-light-hover'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${interactions[selectedWork.id]?.isLiked ? 'fill-red-500' : ''}`} />
                          <span className="hidden sm:inline">{interactions[selectedWork.id]?.isLiked ? '已点赞' : '点赞'}</span>
                        </button>
                        <button
                          onClick={() => handleFavorite(selectedWork.id)}
                          className={`px-3 sm:px-4 py-2 sm:py-2.5 border rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                            interactions[selectedWork.id]?.isFavorited
                              ? 'border-accent-blue bg-accent-blue/10 text-accent-blue hover:bg-accent-blue/20'
                              : 'border-light-border hover:bg-light-hover'
                          }`}
                        >
                          <Bookmark
                            className={`w-4 h-4 ${interactions[selectedWork.id]?.isFavorited ? 'fill-accent-blue' : ''}`}
                          />
                          <span className="hidden sm:inline">{interactions[selectedWork.id]?.isFavorited ? '已收藏' : '收藏'}</span>
                        </button>
                        <button
                          onClick={() => setShowReportModal(true)}
                          className="px-3 sm:px-4 py-2 sm:py-2.5 border border-light-border rounded-xl text-sm font-medium hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors flex items-center gap-2 ml-auto"
                        >
                          <Flag className="w-4 h-4" />
                          <span className="hidden sm:inline">举报</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* AI Remix Modal */}
      {showRemixModal && selectedWork && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowRemixModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b border-light-border px-6 py-4 flex items-center justify-between z-10 shrink-0">
              <div>
                <h3 className="font-semibold text-light-text flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-accent-blue" />
                  AI融合同款生成
                </h3>
                <p className="text-xs text-light-muted mt-0.5">基于「{selectedWork.publicTitle}」进行创意融合</p>
              </div>
              <button onClick={() => setShowRemixModal(false)} className="p-2 hover:bg-light-hover rounded-xl transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* 原作品预览 */}
              <div className="flex items-center gap-3 p-3 bg-light-hover rounded-xl">
                {selectedWork.resultUrl && (
                  <LazyImage
                    src={resolveBrowserMediaUrl(selectedWork.publicImageUrl || selectedWork.resultUrl)}
                    alt=""
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-light-text truncate">{selectedWork.publicTitle}</p>
                  {selectedWork.isPromptPublic && selectedWork.prompt && (
                    <p className="text-xs text-light-muted line-clamp-2 mt-0.5">{selectedWork.prompt}</p>
                  )}
                </div>
              </div>

              {/* 最终提示词编辑区 */}
              {remixPrompt && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-light-text">融合后的提示词</label>
                    <span className="text-xs text-light-muted">可直接编辑</span>
                  </div>
                  <textarea
                    value={remixPrompt}
                    onChange={(e) => setRemixPrompt(e.target.value)}
                    rows={6}
                    className="w-full px-4 py-3 bg-white border border-light-border rounded-xl text-sm text-light-text resize-none focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
                  />
                  <button
                    onClick={handleGenerateRemix}
                    disabled={isGeneratingImage}
                    className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isGeneratingImage ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        生成图片中...
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-4 h-4" />
                        使用此提示词生成图片
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* AI融合输入区 */}
              <div className={`space-y-3 ${remixPrompt ? 'border-t border-light-border pt-4' : ''}`}>
                <label className="text-sm font-medium text-light-text flex items-center gap-2">
                  <Wand2 className="w-4 h-4 text-accent-blue" />
                  描述你的创意需求
                </label>
                <textarea
                  value={remixRequirement}
                  onChange={(e) => setRemixRequirement(e.target.value)}
                  placeholder="例如：保持同样的风格，但把主体换成一只橘猫..."
                  rows={3}
                  className="w-full px-4 py-3 bg-light-hover border border-light-border rounded-xl text-sm text-light-text placeholder-light-muted resize-none focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
                />
                <button
                  onClick={handleRemix}
                  disabled={remixLoading || !remixRequirement.trim()}
                  className="w-full bg-accent-blue/10 text-accent-blue border border-accent-blue/30 hover:bg-accent-blue/20 font-medium py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                >
                  {remixLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      AI融合中...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      {remixPrompt ? '重新融合' : 'AI生成融合提示词'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && selectedWork && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowReportModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h3 className="font-semibold text-light-text">举报作品</h3>
              </div>
              <button onClick={() => setShowReportModal(false)} className="p-2 hover:bg-light-hover rounded-xl">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-light-secondary">
                您正在举报作品「{selectedWork.publicTitle}」，请说明举报原因：
              </p>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="请描述举报原因..."
                rows={4}
                className="w-full px-4 py-3 bg-white border border-light-border rounded-xl text-light-text resize-none focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
              />
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 px-4 py-2.5 border border-light-border rounded-xl text-sm font-medium hover:bg-light-hover transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleReport}
                  className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors"
                >
                  确认举报
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
